import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

import { csp_nonce, env } from '../../../config/AppConfig';
import { getCanonicalUrl } from '../../../core/helpers/CanonicalHelper';
import { CommonImage, JsonValue } from '../../../types/CommonVM';

const CONTENT_CDN = 'https://dom-content-vault-prod-cdn-assets.porter.in';
const FAVICON_URL = `${CONTENT_CDN}/favicon_d9cd692972/favicon_d9cd692972.png`;
const APPLE_TOUCH_ICON_URL = `${CONTENT_CDN}/porterlogo_ef882a1950/porterlogo_ef882a1950.png`;
const FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Titillium+Web:wght@300;400;500;600;700&display=swap';

export interface SeoContent {
  title: string;
  meta_description: string;
  local_data: JsonValue[];
  image: CommonImage;
  is_no_index: boolean;
}

interface SEOCmpProps {
  seo: SeoContent;
}

// Pages still on ReScript data fetchers deliver `schemas` in the ReScript
// list runtime shape (`0` for empty, `{hd, tl}` cons cells — possibly JSON
// round-tripped through getStaticProps), and SEO.t-shaped view models leave it
// undefined. Normalize every shape to a plain array before mapping.
type RescriptList = 0 | { hd: JsonValue; tl: RescriptList };

function toSchemaArray(
  value: JsonValue[] | RescriptList | undefined,
): JsonValue[] {
  if (Array.isArray(value)) {
    return value;
  }
  const schemas: JsonValue[] = [];
  let node = value;
  while (node && typeof node === 'object' && 'hd' in node) {
    schemas.push(node.hd);
    node = node.tl;
  }
  return schemas;
}

// Compliance: don't show certain schemas on truck and bike category pages.
function shouldFilterSchemas(
  locale: string | undefined,
  pathname: string,
): boolean {
  return (
    locale === 'en-IN' &&
    (pathname.includes('/trucks') ||
      pathname.includes('/bikes') ||
      pathname.includes('/two-wheelers'))
  );
}

function SEOCmp({ seo }: Readonly<SEOCmpProps>): React.ReactElement {
  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);
  const filterSchemas = shouldFilterSchemas(router.locale, router.pathname);
  const isProduction = env === 'production' || env === 'prod';

  return (
    <Head>
      <title>{seo.title}</title>
      <meta name="description" content={seo.meta_description} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.meta_description} />
      <meta property="og:image" content={seo.image.path} />
      <meta property="og:image:alt" content={seo.title} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <link rel="canonical" href={canonicalUrl} />
      {!filterSchemas &&
        toSchemaArray(seo.local_data).map(data => {
          const json = JSON.stringify(data);
          // Passing the data to a text node encodes special characters into an
          // html-compatible format, so it is injected as raw html instead.
          return (
            <script
              id="data"
              key={json}
              type="application/ld+json"
              nonce={csp_nonce}
              dangerouslySetInnerHTML={{ __html: json }}
            />
          );
        })}
      <link rel="shortcut icon" href={FAVICON_URL} />
      <link rel="apple-touch-icon" href={APPLE_TOUCH_ICON_URL} />
      <meta charSet="UTF-8" />
      <meta
        name="robots"
        content="max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta
        name="viewport"
        content="width=device-width, maximum-scale=1.0, initial-scale=1.0, user-scalable=no, minimum-scale=1.0"
      />
      <link rel="preconnect" href={CONTENT_CDN} />
      <link rel="dns-prefetch" href={CONTENT_CDN} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href={FONTS_URL} rel="stylesheet" />
      <link rel="manifest" href="/manifest.json" />
      {(!isProduction || seo.is_no_index) && (
        <meta name="robots" content="noindex, nofollow" />
      )}
    </Head>
  );
}

export default SEOCmp;
